<template>
  <el-dialog
    v-model="visible"
    title="Nuevo Prospecto"
    width="50%"
    center
    @close="closeModal"
  >
    <el-form
      ref="formRef"
      :model="prospect"
      :rules="formRules"
      size="default"
    >
      <el-row :gutter="24">
        <el-form-item
          prop="Descripcion"
          class="full-width"
        >
          <custom-input
            v-model="prospect.Descripcion"
            :label="'Cliente/Prospecto'"
            :settings="{
              placeholder: 'Ingresar el cliente',
              maxlength: 125,
              showWordLimit: true,
            }"
          ></custom-input>
        </el-form-item>
      </el-row>
      <el-row
        :gutter="24"
        class="input-margin"
      >
        <el-form-item prop="CodigoSecundario">
          <custom-input
            v-model="prospect.CodigoSecundario"
            :label="'Código Adicional'"
            :settings="{
              placeholder: 'Ingesar código adicional',
              maxlength: 50,
            }"
          ></custom-input>
        </el-form-item>
        <el-form-item prop="CodigoEquivalente">
          <custom-input
            v-model="prospect.CodigoEquivalente"
            :label="'Código equivalente'"
            :settings="{
              placeholder: 'Ingresar código equivalente',
            }"
          />
        </el-form-item>
        <el-form-item prop="Giro">
          <custom-input
            v-model="prospect.Giro"
            :label="'Giro'"
            :settings="{
              placeholder: 'Ingresar giro',
              maxlength: 50,
            }"
          ></custom-input>
        </el-form-item>
        <el-form-item>
          <el-col>
            <div class="custom-label">Activo</div>
            <el-switch v-model="prospect.Activo" />
          </el-col>
        </el-form-item>
      </el-row>
      <el-form-item
        prop="Clasificacion"
        class="input-margin"
      >
        <custom-select
          v-model="prospect.Clasificacion"
          :spanColumn="18"
          :label="'Clasificación'"
          :settings="{
            options: clienteClasificacionList,
          }"
          @update:modelValue="handleClasificacionChange($event)"
        ></custom-select>
      </el-form-item>

      <el-form-item
        prop="CodigoCondicionFiscal"
        class="input-margin"
      >
        <custom-select
          v-model="prospect.CondicionFiscal"
          :spanColumn="18"
          :label="'Condición fiscal'"
          :settings="{
            placeholder: 'Seleccionar condición fiscal (opcional)',
            options: condicionesFiscales,
          }"
          @update:modelValue="handleCondicionFiscalChange($event)"
        />
      </el-form-item>

      <div style="margin-top: 2%">
        <el-form-item
          prop="VendedorPrincipal"
          style="margin-bottom: 2%"
        >
          <custom-ejecutivo-filter
            v-model="prospect.VendedorPrincipal"
            :spanColumn="18"
            label="Vendedor principal"
            :settings="{ placeholder: 'Seleccionar vendedor', size: 'default', isProspect: true }"
          />
        </el-form-item>
        <el-form-item prop="VendedorSecundario">
          <custom-ejecutivo-filter
            v-model="prospect.VendedorSecundario"
            :spanColumn="18"
            label="Vendedor secundario"
            :settings="{ placeholder: 'Seleccionar vendedor', size: 'default', isProspect: true }"
          />
        </el-form-item>
      </div>
      <div class="input-margin">
        <InputContactos
          v-model="prospect.Contactos"
          prop-prefix="Contactos"
        />
      </div>
      <div>
        <CustomLugaresEntregas
          v-model="prospect.LugaresEntrega"
          prop-prefix="LugaresEntrega"
        ></CustomLugaresEntregas>
      </div>
    </el-form>
    <template v-slot:footer>
      <span class="dialog-footer">
        <el-button
          type="primary"
          @click="saveProspect"
          >Guardar</el-button
        >
        <el-button @click="closeModal">Cancelar</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  reactive,
  onBeforeMount,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import InputContactos from '@/shared/components/input_contactos.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import CustomEjecutivoFilter from '@/shared/components/custom_ejecutivo_filter.vue';
import CustomLugaresEntregas from '@/shared/components/custom_lugares_entregas.vue';
import { useClienteClasificacionStore } from '@/stores/cliente-clasificacion';
import { useCondicionFiscalStore } from '@/stores/condicion_fiscal_store';
import { useProspectStore } from '../../views/prospects/stores/prospect_store';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useLoading } from '@/shared/composables/loading.js';
import { ElNotification } from 'element-plus';

const prospectStore = useProspectStore();
const clienteClasificacionStore = useClienteClasificacionStore();
const { firstClienteClasificacion } = clienteClasificacionStore;
const { resetProspect, newProspectHTTP, firstProspects } = prospectStore;
const { prospect } = storeToRefs(prospectStore);
const { clienteClasificacionList } = storeToRefs(clienteClasificacionStore);
const visible = ref(false);
const formRef = ref(null);
const condicionFiscalStore = useCondicionFiscalStore();
const { findAllCondicionFiscalHTTP } = condicionFiscalStore;
const { condicionesFiscales } = storeToRefs(condicionFiscalStore);

const formRules = reactive({
  Descripcion: [{ required: true, message: 'Ingresar cliente', trigger: 'blur' }],
  VendedorPrincipal: [
    { required: true, message: 'Seleccionar vendedor principal', trigger: 'change' },
  ],
});

watch(
  () => prospect.value.LugaresEntrega,
  (list) => {
    Object.keys(formRules)
      .filter((key) => key.startsWith('LugaresEntrega'))
      .forEach((key) => delete formRules[key]);

    list.forEach((_, i) => {
      formRules[`LugaresEntrega.${i}.Descripcion`] = [
        { required: true, message: 'Ingrese descripción', trigger: 'blur' },
      ];
      formRules[`LugaresEntrega.${i}.Provincia`] = [
        { required: true, message: 'Ingrese provincia', trigger: 'blur' },
      ];
      formRules[`LugaresEntrega.${i}.DireccionDetallada`] = [
        { required: true, message: 'Ingrese dirección detallada', trigger: 'blur' },
      ];
    });
  },
  { deep: true, immediate: true },
);

watch(
  () => prospect.value?.Contactos ?? [],
  (list) => {
    const arr = Array.isArray(list) ? list : [];
    Object.keys(formRules)
      .filter((key) => key.startsWith('Contactos'))
      .forEach((key) => delete formRules[key]);
    arr.forEach((c, i) => {
      formRules[`Contactos.${i}.Email`] = [
        { type: 'email', message: 'Email inválido', trigger: 'blur' },
      ];
      formRules[`Contactos.${i}.Nombre`] = [{ max: 80, message: 'Máx 80 car.', trigger: 'blur' }];
      formRules[`Contactos.${i}.Apellido`] = [{ max: 80, message: 'Máx 80 car.', trigger: 'blur' }];
      formRules[`Contactos.${i}.TelefonoPrincipal`] = [
        { max: 30, message: 'Máx 30 car.', trigger: 'blur' },
      ];
      formRules[`Contactos.${i}.TelefonoSecundario`] = [
        { max: 30, message: 'Máx 30 car.', trigger: 'blur' },
      ];
    });
  },
  { deep: true, immediate: true },
);
const emit = defineEmits(['closed']);

const closeModal = () => {
  resetProspect();
  resetFormValidation();
  visible.value = false;
  emit('closed');
};

function handleClasificacionChange(clasificacion) {
  prospect.value.CodigoClasificacion = clasificacion.Codigo;
}

function handleCondicionFiscalChange(item) {
  prospect.value.CodigoCondicionFiscal = item?.Codigo ?? null;
}

const resetFormValidation = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
};

function sanitizeContactos(list = []) {
  return list
    .map((c) => ({
      Codigo: c?.Codigo, // Preservar el Codigo para actualizar contactos existentes
      Nombre: c.Nombre?.trim() || undefined,
      Apellido: c.Apellido?.trim() || undefined,
      Email: c.Email?.trim() || undefined,
      TelefonoPrincipal: c.TelefonoPrincipal?.trim() || undefined,
      TelefonoSecundario: c.TelefonoSecundario?.trim() || undefined,
    }))
    .filter(
      (c) => c.Nombre || c.Apellido || c.Email || c.TelefonoPrincipal || c.TelefonoSecundario,
    );
}

const saveProspect = async () => {
  formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        useLoading().openLoading();
        prospect.value.Contactos = sanitizeContactos(prospect.value.Contactos);
        const response = await newProspectHTTP();
        if (response) {
          useMessageSuccess().open(`Los datos se guardaron existosamente!`);
          closeModal();
        } else {
          useMessageError().open(`El cliente ingresado ya existe`);
        }
      } catch (err) {
        useMessageError().open(`Ha ocurrido un error al intentar guardar los datos.`);
      } finally {
        useLoading().closeLoading();
        await firstProspects();
      }
    } else {
      ElNotification({
        title: 'Nuevo prospecto',
        message: 'Por favor, revise los campos ingresados.',
        type: 'warning',
      });
    }
  });
};

onBeforeMount(async () => {
  firstClienteClasificacion();
  await findAllCondicionFiscalHTTP();
});
</script>

<style scoped>
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  margin: 5%;
}

.el-form {
  margin-top: 0;
}

/* Elimina los márgenes de los el-form-item */
.el-form-item {
  margin-top: 0;
  margin-bottom: 0;
}

.input-margin {
  margin-top: 3%;
}

.full-width {
  width: 100%;
}

.custom-label {
  font-weight: 700;
  color: #111;
}
</style>
